import { useCallback, useMemo, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import { AccordionItem } from "@krea/customer-features/shared-components/accordion/AccordionItem";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import LayoutOneColumnHeader from "@krea/customer-features/shared-components/app/one-column/LayoutOneColumnHeader";
import FactoringBidDetails from "@krea/customer-features/shared-components/bid-dashboards/factoring-bids/FactoringBidDetails";
import LeasingBidDetails from "@krea/customer-features/shared-components/bid-dashboards/leasing-bids/LeasingBidDetails";
import RevolvingCreditBidDetails from "@krea/customer-features/shared-components/bid-dashboards/revolving-credit-bids/RevolvingCreditBidDetails";
import BidDetails from "@krea/customer-features/shared-components/bid-details/BidDetails";
import Button from "@krea/customer-features/shared-components/button";
import ColouredContainer from "@krea/customer-features/shared-components/coloured-container";
import NavigateAction from "@krea/customer-features/shared-components/navigate-action";
import { PostSelectBidCompleted } from "@krea/customer-features/shared-components/post-select/PostSelectBidCompleted";
import PostSelectBidPaidOut from "@krea/customer-features/shared-components/post-select/PostSelectBidPaidOut";
import PostSelectFieldRequests from "@krea/customer-features/shared-components/post-select/PostSelectFieldRequests";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { ILenderResponse } from "@krea/customer-features/types/lenderResponse";
import { LoanApplicationStates } from "@krea/customer-features/types/loanApplication";
import {
  APPLICATION_NAME,
  ScrollToTop,
  BID_TYPE,
} from "@krea/customer-features/utils";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";

import { AlertWithContactPerson } from "../alert-with-contact-person";

interface IPostSelectBidViewProps {
  selectedLenderResponse: ILenderResponse;
  setEligibleForPayment: (data: {
    lenderResponseID: string;
    loanApplicationID: string;
  }) => void;
  isMutatingEligibleForPayment: boolean;
  signingUrlSideEffect?: (data: { lenderResponseID: string }) => void;
}

export const PostSelectBidView = ({
  selectedLenderResponse,
  setEligibleForPayment,
  isMutatingEligibleForPayment,
  signingUrlSideEffect,
}: IPostSelectBidViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMobile();

  const { data: { id: currentLoanApplicationID, state, product } = {} } =
    useGetCachedCurrentLoanApplication();

  const {
    id: selectedLenderResponseID,
    eligibleForPaymentAt,
    signingUrl,
    fieldRequests,
    bid,
    realEstateBid,
    invoicePurchaseBid,
    leasingBid,
    revolvingCreditBid,
  } = selectedLenderResponse;

  const showFieldRequestsToUpdate =
    fieldRequests.filter(
      ({ lenderComment, isCollectedByLender, isSubmitted }) => {
        if (isSubmitted) {
          // show submitted FR only if lenderComment is non-empty (customers can verify that the correct information has been submitted)
          return !!lenderComment;
        } else {
          // only show unsubmitted FR if it is not collected by lender
          return !isCollectedByLender;
        }
      },
    ).length > 0;

  const [eligibleForPaymentView, setEligibleForPaymentView] = useState(
    !!eligibleForPaymentAt || !showFieldRequestsToUpdate,
  );

  const { appName } = settings;
  const isCustomerWeb = appName === APPLICATION_NAME.CUSTOMER_WEB;
  const isPartnerWeb = appName === APPLICATION_NAME.PARTNER_WEB;

  const isPaidOut = state === LoanApplicationStates.PAYOUT_RECEIVED_BY_CUSTOMER;
  const isInPostSelect = state === LoanApplicationStates.BID_SELECTED;

  const fieldRequestsNotCollectedByLender = fieldRequests.filter(
    ({ isCollectedByLender }) => !isCollectedByLender,
  );

  const enableNextStepButton =
    fieldRequests.filter(({ isCollectedByLender, isSubmitted }) => {
      if (isSubmitted) {
        // enable button
        return true;
      } else {
        // when not submitted, enable button if FR isCollectedByLender
        return isCollectedByLender;
      }
    }).length === fieldRequests.length; // button should be enabled only when all FRs are either submitted or collected by lender

  useEffect(() => {
    ScrollToTop();
  }, [eligibleForPaymentView]);

  const hasRun = useRef(false);

  useEffect(() => {
    // If our logic let the user see/land on page 2/2 (eligibleForPaymentView = true)
    // and eligibleForPaymentAt is not set yet, we set it here.

    if (
      eligibleForPaymentView &&
      !eligibleForPaymentAt &&
      !isMutatingEligibleForPayment &&
      !hasRun.current
    ) {
      setEligibleForPayment({
        lenderResponseID: selectedLenderResponseID,
        loanApplicationID: currentLoanApplicationID!,
      });

      hasRun.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleForPaymentView, isMutatingEligibleForPayment]);

  const lenderCompanyName =
    selectedLenderResponse.lenderApplication.lenderCompany.name;

  const pageHeaderText = () => {
    switch (true) {
      case signingUrl && eligibleForPaymentView:
        return t("postSelect.heading.withLenderSigningUrl", {
          lenderCompanyName,
        });

      case !signingUrl && eligibleForPaymentView:
        return t("postSelect.heading.withoutLenderSigningUrl", {
          lenderCompanyName,
        });

      default:
        return t("postSelect.heading.default");
    }
  };

  const goToDashboardTitle = () => {
    switch (true) {
      case signingUrl && eligibleForPaymentView:
        return t(
          "postSelect.actions.goToDashboard.titleEligibleForPaymentWithLink",
        );

      case !signingUrl && eligibleForPaymentView:
        return t(
          "postSelect.actions.goToDashboard.titleEligibleForPaymentNoLink",
        );

      default:
        return t("postSelect.actions.goToDashboard.title");
    }
  };

  const ProductDetailsAccordion = () => {
    const DetailsComponent = useCallback((Component: any) => {
      return (
        <Component
          bid={
            invoicePurchaseBid ??
            realEstateBid ??
            leasingBid ??
            revolvingCreditBid ??
            bid
          }
          isPostSelect={true}
          lenderCompanyName={lenderCompanyName}
        />
      );
    }, []);

    let productType: BID_TYPE = BID_TYPE.COMPANY_LOAN;

    switch (true) {
      case !!bid:
        productType = BID_TYPE.COMPANY_LOAN;
        break;
      case !!realEstateBid:
        productType = BID_TYPE.REAL_ESTATE;
        break;
      case !!leasingBid:
        productType = BID_TYPE.LEASING;
        break;
      case !!revolvingCreditBid:
        productType = BID_TYPE.REVOLVING_CREDIT;
        break;
      case !!invoicePurchaseBid:
        productType = BID_TYPE.FACTORING;
        break;
      default:
        return null;
    }

    let productDetailsTitle;
    let resolvedBidDetailsComponent;

    switch (productType) {
      case BID_TYPE.FACTORING:
        productDetailsTitle = t("postSelect.productDetails.factoring");
        resolvedBidDetailsComponent = useMemo(
          () => DetailsComponent(FactoringBidDetails),
          [],
        );
        break;
      case BID_TYPE.LEASING:
        productDetailsTitle = t("postSelect.productDetails.leasing");
        resolvedBidDetailsComponent = useMemo(
          () => DetailsComponent(LeasingBidDetails),
          [],
        );
        break;
      case BID_TYPE.REVOLVING_CREDIT:
        productDetailsTitle = t("postSelect.productDetails.revolvingCredit");
        resolvedBidDetailsComponent = useMemo(
          () => DetailsComponent(RevolvingCreditBidDetails),
          [],
        );
        break;
      // Real estate and company loans both use BidDetails
      default:
        productDetailsTitle = t("postSelect.productDetails.companyLoan");
        resolvedBidDetailsComponent = useMemo(
          () => DetailsComponent(BidDetails),
          [],
        );
    }

    return (
      <AccordionItem
        titleStyling="tw-font-bold tw-text-md"
        title={productDetailsTitle}
        className="tw-rounded-md tw-border-gray-300"
      >
        {resolvedBidDetailsComponent}
      </AccordionItem>
    );
  };

  return (
    <>
      {isPaidOut ? (
        <PostSelectBidPaidOut />
      ) : (
        <LayoutOneColumn>
          <LayoutOneColumnHeader title={pageHeaderText()} />

          {!eligibleForPaymentView ? (
            <>
              <ColouredContainer
                header={
                  isInPostSelect && (
                    <AlertWithContactPerson
                      variant="info"
                      className="tw-rounded-b-none tw-rounded-t-md tw-border-none tw-bg-purple-50 tw-px-5 has-[*]:tw-my-0"
                      innerText={t("postSelect.toast.requiredInformation")}
                      contactPersonHiddenOnMobile={true}
                    />
                  )
                }
                className={twMerge({ "tw-mb-[70px]": isMobile })}
              >
                <div className="tw-mt-4 tw-px-5">
                  <ProductDetailsAccordion />

                  <div className="tw-pt-5 xl:tw-pb-4">
                    <PostSelectFieldRequests
                      fieldRequests={fieldRequestsNotCollectedByLender}
                    />
                  </div>
                </div>
              </ColouredContainer>

              <div
                style={
                  isMobile
                    ? {
                        boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.10)",
                      }
                    : {}
                }
                className={twMerge({
                  "tw-w-full tw-px-6 tw-bg-white tw-fixed tw-left-0 tw-bottom-0 tw-h-[100px] tw-z-[100000]":
                    isMobile,
                  "tw-flex tw-justify-center": !isMobile,
                })}
              >
                <Button
                  block
                  size="lg"
                  tabIndex={0}
                  className={twMerge("tw-text-center", {
                    "tw-w-full tw-mt-6 tw-mx-auto tw-max-w-[590px] md:tw-max-w-[720px] lg:tw-max-w-[750px]":
                      isMobile,
                    "tw-ml-auto tw-mt-6": !isMobile,
                  })}
                  disabled={!enableNextStepButton}
                  onClick={() => {
                    setEligibleForPaymentView(true);
                  }}
                >
                  {t("postSelect.actions.nextStepCta")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <PostSelectBidCompleted
                selectedLenderResponse={selectedLenderResponse}
                signingUrlSideEffect={() => {
                  if (signingUrlSideEffect) {
                    signingUrlSideEffect({
                      lenderResponseID: selectedLenderResponseID,
                    });
                  }
                }}
                productDetailsAccordion={<ProductDetailsAccordion />}
              />
              {!signingUrl && showFieldRequestsToUpdate && (
                <NavigateAction
                  title={t("postSelect.actions.toPostSelectRequests.title")}
                  className="tw-mt-8"
                  button={{
                    text: t(
                      "postSelect.actions.toPostSelectRequests.buttonText",
                    ),
                    onClick: () => {
                      setEligibleForPaymentView(false);
                    },
                  }}
                />
              )}{" "}
            </>
          )}

          <NavigateAction
            title={goToDashboardTitle()}
            button={{
              text: t("postSelect.actions.goToDashboard.buttonText"),
              onClick: () => {
                if (isCustomerWeb) {
                  navigate("/dashboard");
                } else if (isPartnerWeb) {
                  navigate(
                    `/loan-applications/${currentLoanApplicationID}/dashboard`,
                  );
                }
              },
            }}
            className={twMerge({
              "xl:tw-mt-8 tw--mt-12": signingUrl,
              "tw-mt-8": !signingUrl,
            })}
          />
        </LayoutOneColumn>
      )}

      <div className="tw-mt-[100px] xl:tw-mt-8" />
    </>
  );
};
